import { useCallback, useEffect, useState, useRef } from 'react'
import { isEmpty } from 'lodash'

import { ReactComponent as IconSearch } from 'assets/icons/IconSearch.svg'
import useDebounce from 'hooks/useDebounce'

type TProps = {
  searchQuery: string
  handleSearch: (value: string) => void
}

const SearchBar = ({ searchQuery, handleSearch }: TProps) => {
  const [query, setQuery] = useState('')

  const [searchbarIsOpen, setSearchbarIsOpen] = useState(isEmpty(searchQuery) ? false : true)

  const searchInput = useRef<HTMLInputElement>(null)

  const toggleSearchbarState = useCallback((value: boolean) => {
    setSearchbarIsOpen(value)
  }, [])

  const handleKeyActions = useCallback(
    ({ keyCode, metaKey }: { keyCode: number; metaKey: boolean }): void => {
      const escapeKey = 27
      const cmdKey = metaKey
      const kKey = 75

      if (keyCode === escapeKey && searchbarIsOpen) {
        toggleSearchbarState(false)
        searchInput.current?.blur()
      }

      if (cmdKey && keyCode === kKey && searchInput.current && !searchbarIsOpen) {
        toggleSearchbarState(true)
        searchInput.current.focus()
      }
    },
    [searchbarIsOpen, toggleSearchbarState]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyActions)

    return () => window.removeEventListener('keydown', handleKeyActions)
  }, [searchbarIsOpen, handleKeyActions])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  const debouncedQuery = useDebounce(query, 300)

  useEffect(() => {
    handleSearch(debouncedQuery)
  }, [debouncedQuery])

  useEffect(() => {
    setQuery(searchQuery)
  }, [searchQuery])

  return (
    <div className="search-item">
      <label data-searchbar-is-open={searchbarIsOpen}>
        <IconSearch />
        <input
          ref={searchInput}
          placeholder="Search..."
          type="text"
          value={query}
          onChange={handleInputChange}
          onClick={() => toggleSearchbarState(true)}
        />
        <span className="shortcut">⌘K</span>
      </label>
    </div>
  )
}

export default SearchBar
